import axios, { AxiosInstance, CancelTokenSource } from 'axios';
import async from 'async';
import _, { throttle } from 'lodash';
import systemClock from '@/common/SystemClock';
import { SMH, SMHSDK } from '@/renderer/scripts/smh';
import { httpUrl } from '@/renderer/scripts/http';
import getRequest from '@/renderer/scripts/httpRequest';
import { CommonLoader, UploadConfig } from '@/renderer/scripts/utils/CommonLoader';
import { calcRemainingTime } from '@/renderer/scripts/utils/tools';
import store from '@/renderer/store';
import { IS_BROWSER, IS_PRIVATE, IS_PC, Status } from '@/common/constant';
import { fsStat } from '@/common/listFolders';
import { transformErrorForSerialization } from '@/common/tools';

import { TaskStatus } from '@/renderer/scripts/utils/TaskManager/types';
import { TaskStatusUtil } from '@/renderer/scripts/utils/TaskManager/utils/TaskUtils';
import { uploadStreamManager } from '@/renderer/scripts/utils/StreamManager';
import uploadCache from './cache';
import {
  binaryBase64,
  checkFileExistInBrowser,
  fileSlice,
  FileSteam,
  getUploadCrc64Promise,
  getFileMd5,
  getFileSHA256,
  pathEncode,
} from './utils';

let fs: typeof import('fs');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
}

export default class UploaderClass<T extends Uploader.UploadFileInfo> extends CommonLoader<T> {
  // 上传请求对象
  protected cosRequest: AxiosInstance;
  // 取消请求使用的key
  private confirmKey: string;
  // 文件名冲突时的处理方式
  private conflictResolutionStrategy: string;
  // 高级上传时自动计算当前进行分片上传时真实分片大小
  private chunkSize!: number;

  // smh
  private syncRequestSet: SMH.SyncRequestSet | undefined;
  private commonRequestSet: SMH.CommonRequestSet;
  private syncId: number | undefined;
  private quickUploadResult = 0;
  private quickUploadError = '';

  // 任务开始时间
  private startTimeStamp = Date.now();

  public syncConfirmUploadFileData: SMH.SyncConfirmUploadFileData | undefined;

  /**
   * @description group 表示在私有化时被分到第几组，第 0 组表示已完成的分块
   * @private
   * @memberof UploaderClass
   */
  private multiPartsMap = new Map<number, Uploader.PartsInfo>(); // 保存所有要上传的分块信息 Map

  /**
   * @description (私有化才需要用上)按范围长度对要进行上传的分块内容再进行一次分组
   * @private
   * 结构：若分块有240块，则被分组为 {1: [1,2,3,...,100], 2: [101,102,...,200], 3: [201,...,240]}
   *       若分块中有部分已经上传过了，则可能会被分组为 {1: [141,142,...,200], 2: [201,...,240]}
   * @memberof UploaderClass
   */
  private multiPartsGroup = new Map<number, Set<number>>();

  /**
   * 保存分片上传的上传信息
   */
  private uploadMultiPartInfo: SMH.UploadPartInfo | null = null;

  /**
   * 文件IO进度条
   * @private
   */
  private emitIOProgress;

  constructor(info: T, options: Uploader.options<SMHSDK>) {
    super('upload', info);
    this.info = info;
    this.conflictResolutionStrategy =
      this.info?.conflictResolutionStrategy || options?.conflictResolutionStrategy || 'rename';
    this.syncRequestSet = options.syncRequestSet;
    this.commonRequestSet = options.smh.commonAPI;
    this.syncId = options.syncId;
    this.confirmKey = '';
    this.cosRequest = getRequest({
      maxBodyLength: UploadConfig.COSRequestMaxBodyLength,
      logPrefix: 'COS upload',
    });
    this.emitIOProgress = this._getPercent();

    this.on('status_changed', value => {
      // 若上传任务被 取消 | 暂停 ，则取消请求的发送
      if ([TaskStatus.canceled, TaskStatus.paused].includes(value) && !this.isFinished()) {
        super.cancelRequest();
        this.streamGroup && this.streamGroup.destroyAllStreams();
        if (value === TaskStatus.canceled) {
          // FIX: 暂停时 confirmKey 仍旧在使用状态，因此此处仅取消才移除锁
          uploadCache.removeUsing(this.confirmKey);
        }
      }
    });
  }

  getSMHAPISet() {
    if (this.syncRequestSet) {
      return this.syncRequestSet;
    }
    return this.commonRequestSet;
  }

  /**
   * @description 检查要上传的文件夹路径是否可用 <h2>[remote check]</h2>
   * @private
   * @return {*}  {Promise<boolean>}
   * @memberof UploaderClass
   */
  protected async checkIfUploadAvailable(): Promise<boolean> {
    if (!this.isRunning()) return false;
    const { remoteRootFolder, remoteFolder, spaceId = '' } = this.info;
    if (remoteRootFolder === '') return true;
    try {
      // 1. 检查要上传的根路径是否存在
      await this.getSMHAPISet().inspectDirectory({
        spaceId,
        dirPath: remoteRootFolder,
        cancelToken: this.cancelRequestSource.token,
        collectToken: store.getters.collectToken,
      });
      if (remoteRootFolder === remoteFolder) return true;

      // 2. 检查要上传的目录是否存在（不存在则创建，存在也不抛错
      await this.getSMHAPISet().createDirectory({
        dirPath: remoteFolder,
        conflictResolutionStrategy: 'ask',
        spaceId,
        collectToken: store.getters.collectToken,
      });
      return true;
    } catch (error: any) {
      if (error.message === 'canceled') return false;
      if (error.status === 409 || error.code === 'SameNameDirectoryOrFileExists') {
        return true;
      }
      const err = {
        ...error,
        interface: 'SMH.checkIfUploadAvailable',
      };
      if (err.status === 404) {
        // 根路径不存在，则文件夹上传抛出对应错误码
        err.code = 'InvalidDestinationPath';
      }
      if (err.status === 403) {
        // 根路径无权限访问，则文件夹上传抛出对应错误码
        if (store.getters.collectToken) {
          err.code = 'CollectDirectoryNotBelongUser';
        } else {
          err.code = 'NoPermission';
        }
      }
      return Promise.reject(err);
    }
  }

  /**
   * @description 创建文件夹
   * @private
   * @memberof UploaderClass
   */
  private async createFolder() {
    if (!this.isRunning()) return;
    const initCreateFolder = () => {
      this.setStatus(TaskStatus.success);
      this.emitProgress(this.info.size, { immediately: true });
      this.emit('success', {
        uploadType: 'createFolder',
      });
    };

    this.setStatus(TaskStatus.running);
    try {
      const isValid = await this.checkIfUploadAvailable();
      if (isValid) {
        const { remotePath, spaceId = '' } = this.info;
        await this.getSMHAPISet().createDirectory({
          dirPath: remotePath,
          conflictResolutionStrategy: 'ask',
          spaceId,
        });
        initCreateFolder();
      }
      // 文件夹创建结束，也是一个任务，也要结束
      this.setStatus(TaskStatus.success);
    } catch (error: any) {
      if (error.status === 409 || error.code === 'SameNameDirectoryOrFileExists') {
        initCreateFolder();
        return;
      }
      if (error.message === 'canceled') return;

      this.setStatus(TaskStatus.error);
      this.emit('error', {
        ...error,
        uploadType: 'createFolder',
      });
    }
  }

  /**
   * @description 简单上传
   * @private
   * @memberof Uploader
   */
  private async simpleUpload() {
    if (!this.isRunning()) return;

    const { remotePath, localPath, size: filesize, body } = this.info;
    const uploadToCOS = async (retry: number) => {
      let loaded = 0;
      let uploadData: SMH.UploadFileData;
      try {
        // 1. 检查文件夹路径是否存在
        const isValid = await this.checkIfUploadAvailable();
        if (!isValid) return;
        // 警告，上传状态必须紧跟上传逻辑，中间不可插入逻辑
        this.setStatus(TaskStatus.running);
        // 2. 取 smh 上传信息
        const res = await this.requestSMHUploadFile({
          filePath: remotePath,
          filesize,
          conflictResolutionStrategy: this.conflictResolutionStrategy,
          spaceId: this.info.spaceId,
          ...(typeof this.syncId === 'number' && {
            syncId: this.syncId,
          }),
          cancelToken: this.cancelRequestSource.token,
          collectToken: store.getters.collectToken,
        });
        uploadData = res.data;
      } catch (error: any) {
        if (error.message === 'canceled') return;

        this.setStatus(TaskStatus.error);
        this.emit('error', {
          ...error,
          interface: 'SMH.uploadFile',
          uploadType: 'simple',
        });
        return;
      }
      this.confirmKey = uploadData.confirmKey;
      const url = `${httpUrl.protocol}://${uploadData.domain.concat(pathEncode(uploadData.path))}`;
      let rStream;
      let clientStream;
      try {
        this.emitProgress(0, { immediately: true });
        if (IS_BROWSER) {
          // 校验本地文件是否变更，会导致上传失败
          await checkFileExistInBrowser(this.info.body as Blob);
          await this.cosRequest({
            url,
            method: 'PUT',
            headers: {
              ...uploadData.headers,
            },
            data: body,
            cancelToken: this.cancelRequestSource.token,
            onUploadProgress: (progress: any) => this.emitProgress(progress.loaded),
          });
        } else {
          // PC 简单上传
          if (IS_BROWSER) {
            rStream = fs.createReadStream(localPath as string);
          } else {
            if (this.streamGroup) {
              const { limitStream } = this.streamGroup.createLimitStream({
                type: 'upload',
                path: localPath,
              });
              clientStream = limitStream;
            }
          }

          await this.cosRequest({
            url,
            method: 'PUT',
            headers: {
              ...uploadData.headers,
              'Content-Length': filesize,
            },
            // 设置maxRedirects =0 才会直接使用原声的http(s)模块，否则使用的是follow-redirects（这个模块没有进度功能）
            maxRedirects: 0,
            data: IS_BROWSER ? rStream : clientStream,
            cancelToken: this.cancelRequestSource.token,
            onUploadProgress: (progress: { loaded: number; total: number; speed: number; percent: number }) => {
              loaded += progress.loaded;
              this.emitProgress(progress.loaded);
            },
          });
          if (rStream) {
            rStream.close();
            rStream.destroy();
          }
        }
        this.setStatus(TaskStatus.preparing);
        const finished = await this.finishUpload({
          uploadType: 'simple',
        });
        if (finished) {
          this.setStatus(TaskStatus.success);
          this.emit('success', { uploadType: 'simple' });
        }
      } catch (error: any) {
        if (rStream) {
          rStream.close();
          rStream.destroy();
        }

        if ([TaskStatus.paused, TaskStatus.success].includes(this.status)) return;
        // 重试一次：上传到 COS 的签名超时 | 5xx 服务器错误
        // 公有云超时判断 || 私有云超时判断
        const isExpired =
          (error.status === 403 && error.message === 'Request has expired') || error.code === 'RequestTimeTooSkewed';

        // 需要重试：签名过期|5xx 服务器错误
        const allowRetry = isExpired || Math.floor(error.status / 100) === 5;
        // 不需要重试：待丰富，可根据status和code按需禁止
        const forbidRetry = error.status === 409;
        if (allowRetry) {
          if (retry < UploadConfig.ChunkRetryTimes) {
            uploadToCOS(retry + 1);
          } else {
            if (error.message === 'canceled') return;

            this.setStatus(TaskStatus.error);
            this.emit('error', {
              ...error,
              interface: 'COS.putObject',
              uploadType: 'simple',
            });
            this.cancel(true, true); // 不设置状态为cancel
          }
        } else if (body?.size === 0) {
          // windows端系统web上传文件夹，路径名称过长时，无法获取文件大小，导致上传失败，添加失败提示
          if (error.message === 'canceled') return;
          error.code = 'FileSizeZero';
          this.setStatus(TaskStatus.error);
          this.emit('error', {
            ...error,
            interface: 'COS.putObject',
            uploadType: 'simple',
          });
        } else if (!forbidRetry && retry < UploadConfig.ChunkRetryTimes) {
          uploadToCOS(retry + 1);
        } else {
          if (error.message === 'canceled') return;
          this.setStatus(TaskStatus.error);
          if (!error.code || !error.message) {
            // FIX: 修复有时因为没有找到 code 和 message 而无法打出错误码的问题
            // eslint-disable-next-line no-ex-assign
            error = transformErrorForSerialization(error);
          }
          this.emit('error', {
            ...error,
            interface: 'COS.putObject',
            uploadType: 'simple',
          });
          this.cancel(true, true);
        }
      }
    };
    uploadToCOS(0);
  }

  /**
   * 根据文件本体，自动计算文件分块大小
   * @param FileSize
   * @param ChunkSize
   * @private
   */
  private _calcAutoChunkSize(FileSize: number, ChunkSize: number): number {
    const SIZE = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 1024 * 2, 1024 * 4, 1024 * 5];
    let AutoChunkSize = 1024 * 1024;
    for (let i = 0; i < SIZE.length; i++) {
      AutoChunkSize = SIZE[i] * 1024 * 1024;
      if (FileSize / AutoChunkSize <= UploadConfig.MaxPartNumber) break;
    }
    if (typeof window.uploadChunkSize !== 'undefined') {
      if (FileSize / window.uploadChunkSize > UploadConfig.MaxPartNumber) {
        /* i18n-ignore*/
        throw new Error('debug 上传设置的 chunkSize 过小，导致分块数量超过 1w块，请检查');
      }
      return window.uploadChunkSize;
    }
    return Math.max(ChunkSize, AutoChunkSize);
  }
  // 校验文件完整性
  // private async _checkFileIntegrity() {}
  /**
   * @description 分块上传
   * @memberof Uploader
   */
  private async advanceUpload(startTime?: any) {
    const uploadType = 'advanced';
    if (!this.isRunning()) return;

    const { ChunkSize } = UploadConfig;
    const FileSize = this.info.size;
    if (!this.chunkSize) {
      // 计算真正的分片大小 (控制分片大小，当文件过大导致按照5MB一个分片，分片总数会超过1W个，而COS最多支持1W个分片，此时自行计算出分片的大小)
      this.chunkSize = this._calcAutoChunkSize(FileSize, ChunkSize);
    }
    // 文件分块数
    const sliceCount = Math.ceil(FileSize / this.chunkSize);
    const handlerError = (error: MsgError) => {
      this.setStatus(TaskStatus.error);
      uploadCache.removeUsing(this.confirmKey);
      if (error instanceof DOMException) {
        const { name: code } = error;
        this.emit('error', {
          ...{
            code,
          },
          uploadType,
        });
      } else {
        this.emit('error', {
          ...error,
          uploadType,
        });
      }
    };
    try {
      // 1. 检查根目录是否存在
      const isValid = await this.checkIfUploadAvailable();
      if (!isValid) return;

      // 2. 开始获取文件分片上传信息
      const { canFinish } = await this.getUploadPartInfo(sliceCount);
      // TODO loganylwu 这里的Running其实不是真的Running，其实任务还没有开始运行，但是目前受限于任务，没时间改，先mark
      if (!this.isRunning()) return;
      const uuid = uploadCache.getFileId(this.info, this.chunkSize);
      if (uuid) {
        uploadCache.saveConfirmKey(uuid, this.confirmKey);
      } else {
        const error = {
          code: 'NODE.getUUID',
        };
        handlerError(error);
        return;
      }
      uploadCache.setUsing(this.confirmKey);

      // 3. 上传所有分片+并行计算crc64
      let crc64Str = '';
      if (!canFinish) {
        // 警告，状态切换必须紧跟上传过程
        // =========loader:preparing->running========
        this.setStatus(TaskStatus.running);
        if (!IS_BROWSER) {
          [crc64Str] = await Promise.all([
            getUploadCrc64Promise(this.info.localPath, null),
            this.uploadAllParts(sliceCount),
          ]);
        } else {
          await this.uploadAllParts(sliceCount);
        }
      } else {
        if (!IS_BROWSER) {
          // TODO: 后面优化需要将crc64的计算进度也需要给出，当大文件处理时可以显示出进度
          crc64Str = await getUploadCrc64Promise(this.info.localPath, null);
        }
      }

      if (!this.isRunning()) return;
      // 4. 结束本次上传
      // =========loader:running->preparing========
      this.setStatus(TaskStatus.preparing);
      const finished = await this.finishUpload({
        uploadType: 'advanced',
        crc64: crc64Str,
      });
      if (finished) {
        this.setStatus(TaskStatus.success);
        this.emit('success', { uploadType });
        uploadCache.removeConfirmKey(this.confirmKey);
      }
    } catch (error: any) {
      if (error.message === 'canceled') return;
      logger.ui.error(`[Uploader] [${this.info.name}] advanceUpload error:`, JSON.stringify(error));
      handlerError(error);
    }
  }

  /**
   * @description 获取上传分片信息
   * @private
   * @param {number} sliceCount 分块数
   * @return {{ canFinish: boolean }} canFinish是否块已传完，true: 可以结束 false: 需要进行分块上传
   * @memberof UploaderClass
   */
  private async getUploadPartInfo(sliceCount: number) {
    const { remotePath, localPath, size } = this.info;
    // 计算 ETag
    const ETagMap: Record<number, string> = {};
    let FinishSize = 0;

    /**
     * @description 获取本地缓存的可用于上传的分块信息
     * @return {*}  {(Promise<SMH.GetFileUploadStatusData | null>)}
     */
    const getLocalAvailUploadPartInfo = async (): Promise<SMH.GetFileUploadStatusData | null> => {
      let statusData: SMH.GetFileUploadStatusData | null = null;
      const getFileUploadStatus = async (confirmKey: string): Promise<boolean> => {
        try {
          const res = await this.getSMHAPISet().getFileUploadStatus({
            confirmKey,
            spaceId: this.info.spaceId,
            ...(typeof this.syncId === 'number' && {
              syncId: this.syncId,
            }),
            // 私有化时不展示 uploadPartInfo 字段
            ...(IS_PRIVATE && {
              noUploadPartInfo: 1,
            }),
          });
          statusData = res.data;
          if (statusData.uploadPartInfo) {
            // 公有云接口的 getFileUploadStatus 返回才有 uploadPartInfo 字段
            this.uploadMultiPartInfo = statusData.uploadPartInfo;
          }
          this.confirmKey = confirmKey;
          return true;
        } catch (error: MsgError) {
          // 如果 confirmKey 获取出错，跳过并删除
          uploadCache.removeConfirmKey(this.confirmKey);
          return false;
        }
      };
      if (this.confirmKey) {
        await getFileUploadStatus(this.confirmKey);
      } else {
        // 在本地找可用的 UploadId
        const uuid = uploadCache.getFileId(this.info, this.chunkSize);
        const LocalConfirmKeyList = uploadCache.getConfirmKeyList(uuid);
        if (!uuid || !LocalConfirmKeyList.length) {
          return null;
        }
        await async.detectLimit(LocalConfirmKeyList, 1, (confirmKey, callback) => {
          // 如果正在上传，跳过
          if (confirmKey in uploadCache.using) {
            callback(null, false);
          } else {
            getFileUploadStatus(confirmKey).then(flag => callback(null, flag));
          }
        });
      }
      return statusData;
    };

    /**
     * @description 获取本地文件某个分片的信息
     * @return {*}  {(Promise<{ PartNumber, ETag, Size }>)}
     */
    const getChunkETag = async (PartNumber: number) => {
      const start = this.chunkSize * (PartNumber - 1);
      const end = Math.min(start + this.chunkSize, size);
      const ChunkSize = end - start;
      if (ETagMap[PartNumber]) {
        return {
          PartNumber,
          ETag: ETagMap[PartNumber],
          Size: ChunkSize,
        };
      }
      const chunkBody = fileSlice(
        {
          filePath: localPath as string,
          body: this.info.body,
        },
        start,
        end,
      );

      const md5 = await getFileMd5(chunkBody);
      const ETag = `"${md5}"`;
      ETagMap[PartNumber] = ETag;
      FinishSize += ChunkSize;
      this.emitHashProgress(FinishSize);
      // onHashProgress({ loaded: FinishSize, total: FileSize });
      return {
        PartNumber,
        ETag: ETagMap[PartNumber],
        Size: ChunkSize,
      };
    };

    /**
     * 根据parts参数，读取本地文件分片，对比ETag值是否一致
     * @param parts
     * @return {*}  {(Promise<boolean | void>)}
     */
    const checkFilePartsOnDisk = async (parts: SMH.Parts[]) => {
      const availableParts: SMH.Parts[] = [];
      const partCount = parts.length;
      // 如果没有分片，通过
      if (partCount === 0) {
        return availableParts;
      }
      // 检查分片数量
      if (partCount > sliceCount) {
        return availableParts;
      }
      // 检查分片大小
      if (partCount > 1) {
        const PartChunkSize = Math.max(parts[0].Size, parts[1].Size);
        if (PartChunkSize !== this.chunkSize) {
          return availableParts;
        }
      }

      let tmpCount = 0;
      // 逐个分片计算并检查 ETag 是否一致，不一致的分块丢弃掉，一致的保留
      await async.eachLimit(parts, 16, (part, next) => {
        const goNext = (e?: Error) => {
          // e之前触发一下计数器
          this.emitIOProgress(tmpCount++, parts.length);
          next(e);
        };
        if (!this.isRunning()) {
          // 为了立即停止当前each循环，直接抛出canceled错误对当前逻辑无影响
          goNext(new Error('canceled'));
          return;
        }
        getChunkETag(part.PartNumber)
          .then(chunk => {
            if (chunk) {
              const { ETag, Size } = chunk;
              if (ETag !== part.ETag || Size !== part.Size) {
                goNext();
              } else {
                availableParts.push(part);
                goNext();
              }
            } else {
              goNext();
            }
          })
          .catch(error => {
            const err = {
              ...error,
              interface: 'NODE.getChunkETag',
            };
            goNext(err);
          });
      });
      return availableParts;
    };

    /**
     * @description 读取本地信息，若与缓存信息不匹配则更新缓存
     */
    const updateFileInfo = () => {
      if (IS_BROWSER) return;
      const stat = fs.statSync(this.info.localPath);
      const { size, mtime: mtimeDate } = stat;
      const mtime = mtimeDate.getTime();
      if (this.info.size !== size) {
        this.info.size = size;
      }
      if (this.info.mtime !== mtime) {
        this.info.mtime = mtime;
      }
    };

    /**
     * @description 计算所有要上传的分块的信息
     * @return {*}  {(PartsInfo[])}
     */
    const computedAllUploadData = (partList: SMH.Parts[]): void => {
      this.multiPartsMap = new Map<number, Uploader.PartsInfo>();
      this.multiPartsGroup = new Map<number, Set<number>>();
      const uploadedPartNum: number[] = []; // 已完成的分片
      partList.forEach(part => {
        uploadedPartNum.push(part.PartNumber);
        this.multiPartsMap.set(part.PartNumber, {
          group: 0,
          ETag: part.ETag,
          PartNumber: part.PartNumber,
          uploaded: true,
        });
      });
      logger.ui(`[Uploader] [${this.info.name}] finished upload parts: `, uploadedPartNum.join(','));
      let currGroupIndex = 1; // 当前分组索引
      for (let PartNumber = 1; PartNumber <= sliceCount; PartNumber++) {
        const part = this.multiPartsMap.get(PartNumber);
        if (!part?.ETag) {
          this.multiPartsMap.set(PartNumber, {
            group: currGroupIndex,
            PartNumber,
            ETag: undefined,
            uploaded: false,
          });

          // 私有化时需要对分块进行分组，此处不管公私都进行分组处理
          const rangeSet = this.multiPartsGroup.get(currGroupIndex);
          if (rangeSet) {
            if (rangeSet.size < UploadConfig.MaxGroupPartRange) {
              rangeSet.add(PartNumber);
            } else {
              currGroupIndex += 1;
              this.multiPartsGroup.set(currGroupIndex, new Set([PartNumber]));
            }
          } else {
            this.multiPartsGroup.set(currGroupIndex, new Set([PartNumber]));
          }
        }
      }
    };

    /**
     * @description 初始化新的分片上传任务
     * @return {*}  {Promise<{ canFinish: boolean }>}
     */
    const initUploadPartInfo = async () => {
      try {
        const res = await this.requestSMHUploadMultiPartFile({
          filePath: remotePath,
          filesize: this.info.size,
          conflictResolutionStrategy: this.conflictResolutionStrategy,
          spaceId: this.info.spaceId,
          ...(typeof this.syncId === 'number' && {
            syncId: this.syncId,
          }),
          // 私有化进行初始化时需要单独取出'1-MaxGroupPartRange'块的签名头部
          ...(IS_PRIVATE && {
            partNumberRange: `1-${UploadConfig.MaxGroupPartRange}`,
          }),
          collectToken: store.getters.collectToken,
        });
        const uploadData = res.data;
        this.confirmKey = uploadData.confirmKey;
        this.uploadMultiPartInfo = uploadData;
        computedAllUploadData([]);
        return { canFinish: false };
      } catch (error: MsgError) {
        const err = {
          ...error,
          interface: 'SMH.uploadMultiPartFile',
        };
        return Promise.reject(err);
      }
    };

    // 第一步：读取本地信息，若与缓存信息不匹配则更新缓存
    updateFileInfo();

    // 第二步：从本地缓存获取文件分块上传任务状态信息
    const statusData = await getLocalAvailUploadPartInfo();

    // 第三步：判断本地缓存是否存在
    if (statusData) {
      const { confirmed, uploadId } = statusData;
      if (!confirmed) {
        // 如果confirm=false,uploadId=''，则表示分块上传已经结束，仅需要finishUpload
        if (!uploadId) return { canFinish: true };

        const availableParts = await checkFilePartsOnDisk(statusData.parts);
        if (availableParts.length > 0) {
          // 第 3.1 步：当远端分片缓存可用时计算所有分片信息
          computedAllUploadData(availableParts);

          if (IS_PRIVATE) {
            // 私有化需要调用 renew 拉取要上传的部分分块签名头部
            await this.updateUploadMultiPartInfo();
          }
          return { canFinish: false };
        }
      }

      // 第 3.2 步：当缓存不可用时删除缓存并检查是否能初始化新的分片上传任务
      uploadCache.removeConfirmKey(this.confirmKey);

      // 第 3.3 步：如果是已经上传用过并且已结束过的confirmkey则需重新获取confirmKey上传
    }

    // 第四步：若本地无缓存，初始化新的分片上传任务
    return initUploadPartInfo();
  }

  /**
   * @description 上传所有分片
   * @private
   * @memberof Uploader
   */
  private async uploadAllParts(sliceCount: number): Promise<void> {
    if (this.status !== TaskStatus.running) {
      throw new Error('canceled');
    }
    const resetPartGroup = (part: Uploader.PartsInfo) => {
      // 将当前 part 从分组中移出，并重置其分组索引为已完成的分组
      const groupArr = this.multiPartsGroup.get(part.group);
      groupArr?.delete(part.PartNumber);
      part.group = 0;
    };
    const { size } = this.info;
    const { chunkSize } = this;
    // 已上传的文件大小
    let FinishSize = 0;
    this.emitProgress(0, { immediately: true });
    if (this.multiPartsMap.size > 0) {
      const needUploadParts = [...this.multiPartsMap.values()].filter(part => {
        if (part.uploaded) {
          FinishSize += part.PartNumber >= sliceCount ? size % chunkSize || chunkSize : chunkSize;
        }
        return !part.uploaded;
      });
      // 模拟已经上传的部分（其实没有上传，只是模拟已上传）
      this.emitProgress(0, { immediately: true, init: true, startSize: FinishSize });

      // @ts-ignore 增加埋点，可通过调试控制台修改参数
      const ChunkParallelLimit = window.ChunkParallelLimit || UploadConfig.ChunkParallelLimit;
      logger.ui(
        `分块上传`,
        `localPath:${this.info.localPath} | size:${size} | chunkSize:${this.chunkSize} | needUploadParts:${needUploadParts.length} | allSize:${this.multiPartsMap.size}`,
      );

      let shouldStopByCancel = false; // 上传已取消时，eachLimit循环要停下来
      const asyncTimeStamp = this.startTimeStamp;
      await async.eachLimit(needUploadParts, ChunkParallelLimit, (part, next) => {
        if (this.status !== TaskStatus.running || shouldStopByCancel || asyncTimeStamp !== this.startTimeStamp) {
          next(new Error('canceled'));
          return;
        }

        const currentSize = Math.min(size, part.PartNumber * chunkSize) - (part.PartNumber - 1) * chunkSize;
        let preAddSize = 0;
        // 把每个Process内的数据，都计算到FinishSize中，最后emit出去
        const onProgress = (_loaded: number) => {
          FinishSize += _loaded - preAddSize;
          preAddSize = _loaded;
          this.emitProgress(FinishSize);
        };
        try {
          this.uploadPart(part, onProgress)
            .then(res => {
              const { headers } = res as any;
              FinishSize += currentSize - preAddSize;
              part.ETag = headers.etag;
              part.uploaded = true;
              resetPartGroup(part);
              this.emitProgress(FinishSize);
              next();
            })
            .catch(error => {
              if (error?.message === 'canceled') {
                shouldStopByCancel = true;
              }
              FinishSize -= preAddSize;
              this.emitProgress(FinishSize);
              if (error.code === 'ENOENT') {
                // 上传中，文件被操作引起的错误处理
                error.code = 'NoSuchFileOrDirectory';
                error.interface = 'SMH.NoSuchFileOrDirectory';
              }
              next(error);
            });
        } catch (error: any) {
          FinishSize -= preAddSize;
          this.emitProgress(FinishSize);
          next(error);
        }
      });
    }
  }
  private async uploadPart(part: Uploader.PartsInfo, onProgress: (loaded: number) => void) {
    const { PartNumber } = part;
    const { chunkSize } = this;
    const { size, localPath, body } = this.info;
    const start = chunkSize * (PartNumber - 1);
    let end = start + chunkSize;
    let ContentLength = chunkSize;
    if (end > size) {
      end = size;
      ContentLength = end - start;
    }

    // 校验本地文件是否变更，会导致上传失败
    if (IS_BROWSER) {
      await checkFileExistInBrowser(this.info.body as Blob);
    } else {
      this.checkFileChangedThrottle();
    }

    const chunkBody = await fileSlice(
      {
        filePath: localPath as string,
        body,
      },
      start,
      end,
    );

    const md5 = await getFileMd5(chunkBody);
    const contentMd5 = md5 ? binaryBase64(md5) : '';
    let retryTimes = -1; // 保存重试次数
    let shouldStopByCancel = false; // 上传已取消时，重试要停下来
    const asyncTimeStamp = this.startTimeStamp;
    return async.retry(
      {
        times: UploadConfig.ChunkRetryTimes,
        interval: 1000, // 重试间隔 1s
      },
      done => {
        retryTimes++;
        if (!this.isRunning() || shouldStopByCancel || asyncTimeStamp !== this.startTimeStamp) {
          done(new Error('canceled'));
          return;
        }
        let currentStreamBeanId = '';
        /**
         *  把一个part上传到COS桶
         */
        const uploadToCos = async () => {
          // 当前文件part上传了多少bytes
          let loaded = 0;
          let stream: Blob | unknown = null;
          let data: FileSteam;
          if (IS_BROWSER) {
            const sliceData = await fileSlice(
              {
                filePath: localPath as string,
                body,
              },
              start,
              end,
            );

            stream = sliceData.stream;
            data = sliceData;
          } else {
            if (this.streamGroup) {
              // 创建上传流
              const { limitStream, id: streamBeanId } = this.streamGroup.createLimitStream(
                { type: 'upload', path: localPath },
                { start, end: end - 1 },
              );

              currentStreamBeanId = streamBeanId;
              stream = limitStream;
            }
          }

          const {
            domain,
            path,
            uploadId,
            headers,
            parts: partsHeaderInfo,
          } = this.uploadMultiPartInfo as SMH.UploadPartInfo;
          const url = `${httpUrl.protocol}://${domain.concat(pathEncode(path))}`;
          let finalHeaders = headers;
          // 私有云需要单独取分片对应的header头部
          if (IS_PRIVATE) {
            finalHeaders = partsHeaderInfo[part.PartNumber]?.headers;
            if (!finalHeaders) {
              try {
                await this.updateUploadMultiPartInfo(part);
                if (this.uploadMultiPartInfo) {
                  finalHeaders = this.uploadMultiPartInfo.parts[part.PartNumber].headers;
                }
              } catch (error: MsgError) {
                done(error);
                return;
              }
            }
          }
          this.cosRequest({
            url,
            method: 'PUT',
            headers: {
              ...finalHeaders,
              'Content-MD5': contentMd5,
              ...(!IS_BROWSER && {
                'Content-Length': ContentLength,
              }),
            },
            data: stream,
            cancelToken: this.cancelRequestSource.token,
            // 200s 超时即重试（COS后台策略为 200S传输内容小于4MB后端主动断开链接）
            // timeout: 200 * 1000,
            timeout: 60 * 1000,
            params: {
              partNumber: part.PartNumber,
              uploadId,
            },
            onUploadProgress: (progress: any) => {
              loaded += progress.loaded;
              onProgress(progress.loaded);
            },
          })
            .then(res => {
              done(null, res);
            })
            .catch(err => {
              const error = {
                ...transformErrorForSerialization(err),
                interface: 'COS.uploadPart',
              };
              // 公有云超时判断 || 私有云超时判断
              const isExpired =
                (err.status === 403 && err.message === 'Request has expired') || err.code === 'RequestTimeTooSkewed';

              // 超时 || 服务器 5xx 错误
              if (isExpired || Math.floor(err.status / 100) === 5) {
                // 第一次因为超时重试时更新分块所处组的所有header鉴权头部
                let _part: Uploader.PartsInfo | number = part;
                // 非超时 || 第二次超时重试 更新当前分块的header鉴权头部
                if (!isExpired || (retryTimes > 1 && isExpired)) {
                  _part = part.PartNumber;
                }
                // 更新完分块信息后重试
                this.updateUploadMultiPartInfo(_part, isExpired)
                  .then(async () => {
                    done(error);
                  })
                  .catch(error => {
                    done(error);
                  });
              } else {
                if (error.message === 'canceled') {
                  shouldStopByCancel = true;
                }
                done(error);
              }
              /* i18n-ignore*/
              IS_PRIVATE && logger.ui(`[Uploader] 重试分块索引: ${part.PartNumber}, 重试次数: ${retryTimes}`);
            })
            .finally(() => {
              if (IS_BROWSER) {
                if (data && data?.type === 'ReadStream') {
                  data.stream.close();
                  data.stream.destroy();
                }
              } else {
                this.streamGroup && this.streamGroup.destroyStreamByBeanId(currentStreamBeanId);
              }
            });
        };
        uploadToCos();
      },
    );
  }

  /**
   * @description
   * @private
   * @param {(Uploader.PartsInfo | number)} [part]
   * 私有云才有可能需要传 part 参数：
   * 当 part 是 Uploader.PartsInfo 类型表示更新 part 所在组的所有part鉴权头部（若无对应组则仅更新part自己）
   * 当 part 是 number 时表示更新 number 对应 part 的header鉴权头部；
   * 当 part 为空则直接取未上传的分组中分组索引为 1 的所有part鉴权头部
   * @param {(boolean)} [forceRefresh] 强制刷新
   * @return {*}  {Promise<void>}
   * @memberof UploaderClass
   */
  private async updateUploadMultiPartInfo(part?: Uploader.PartsInfo | number, forceRefresh?: boolean): Promise<void> {
    let partNumberRange!: string;
    if (IS_PRIVATE) {
      if (part) {
        if (typeof part !== 'number') {
          const groupIndex = part.group;
          const numbers = this.multiPartsGroup.get(groupIndex);
          if (numbers?.has(part.PartNumber)) {
            partNumberRange = [...(numbers || [])]?.join(',');
          } else {
            partNumberRange = part.PartNumber.toString();
          }
        } else {
          partNumberRange = part.toString();
        }
      } else {
        partNumberRange = [...(this.multiPartsGroup.get(1) || [])].join(',');
        if (!partNumberRange) return undefined;
      }
    }
    try {
      if (this.uploadMultiPartInfo) {
        let { expiration } = this.uploadMultiPartInfo;
        let hasPartHeader = !IS_PRIVATE;
        if (IS_PRIVATE && part) {
          let partNumber!: number;
          if (typeof part === 'number') {
            partNumber = part;
          } else {
            partNumber = part.PartNumber;
          }
          const partInfo = this.uploadMultiPartInfo.parts[partNumber];
          hasPartHeader = !!partInfo;
          expiration = (partInfo?.expiration as string) || this.uploadMultiPartInfo.expiration;
        }
        const duration = (new Date(expiration).getTime() - systemClock.now()) / 1000;
        // 若当前分片上传签名还有 5 分钟过期 || 私有云环境分片信息中没有对应分片所需的鉴权头部
        if (duration < 5 * 60 || forceRefresh || !hasPartHeader) {
          const { data } = await this.getSMHAPISet().updateMultiPartFileInfo({
            confirmKey: this.confirmKey,
            spaceId: this.info.spaceId,
            ...(typeof this.syncId === 'number' && {
              syncId: this.syncId,
            }),
            ...(partNumberRange && {
              partNumberRange,
            }),
            collectToken: store.getters.collectToken,
          });
          Object.values(data.parts || {}).forEach(part => {
            part.expiration = data.expiration;
          });
          this.uploadMultiPartInfo = {
            ...this.uploadMultiPartInfo,
            ...data,
          };
        }
      } else {
        const res = await this.getSMHAPISet().updateMultiPartFileInfo({
          confirmKey: this.confirmKey,
          spaceId: this.info.spaceId,
          ...(typeof this.syncId === 'number' && {
            syncId: this.syncId,
          }),
          ...(partNumberRange && {
            partNumberRange,
          }),
          collectToken: store.getters.collectToken,
        });
        this.uploadMultiPartInfo = {
          ...res.data,
        };
      }
      if (partNumberRange) {
        /* i18n-ignore*/
        logger.ui(`[Uploader] [${this.info.name}]更新分片鉴权头部：`, partNumberRange);
      } else {
        /* i18n-ignore*/
        logger.ui(`[Uploader] [${this.info.name}]更新分片鉴权头部：`);
      }
      return undefined;
    } catch (err) {
      const error = {
        ...transformErrorForSerialization(err),
        interface: 'SMH.updateMultiPartFileInfo',
      };
      return Promise.reject(error);
    }
  }

  // 通过比较本地文件的修改时间和文件大小来判断文件是否被修改
  private checkFileChanged() {
    if (IS_BROWSER) return;
    let stat!: any;
    try {
      stat = fs.statSync(this.info.localPath);
    } catch (error) {
      throw {
        code: 'NODE.GetFileStatError',
        interface: 'SMH.checkFileChanged',
      };
    }
    const { mtime, size } = stat;
    const mtimeChanged = mtime.getTime() !== this.info.mtime;
    const sizeChanged = size !== this.info.size;
    const isChanged = mtimeChanged || sizeChanged;
    if (isChanged) {
      throw {
        code: 'NODE.FileContentChanged',
        interface: 'SMH.checkFileChanged',
      };
    }
  }

  // 在上传过程中每5秒检查一次文件是否已经被改变
  private checkFileChangedThrottle = throttle(this.checkFileChanged.bind(this), 5000);

  /**
   * @description 本次上传结束
   * @private
   * @memberof Uploader
   */
  private async finishUpload(params: CoFile.UploadFinishedParams): Promise<boolean> {
    let finished = false;
    if (!this.isRunning()) return false;
    const { uploadType, crc64 } = params;
    try {
      // 高级上传需在上传结束时检查文件变化
      if (uploadType === 'advanced') {
        this.checkFileChanged();
      }
      const res = await this.getSMHAPISet().confirmUploadFile({
        confirmKey: this.confirmKey,
        conflictResolutionStrategy: this.conflictResolutionStrategy,
        cancelToken: this.cancelRequestSource.token,
        spaceId: this.info.spaceId,
        ...(IS_PC && { crc64 }),
        ...(typeof this.syncId === 'number' && { syncId: this.syncId }),
        collectToken: store.getters.collectToken,
      });
      if (!res?.data?.path) {
        return Promise.reject({
          code: 'DirectoryNotFound',
          interface: 'SMH.confirmUploadFile',
          uploadType,
        });
      }
      if (this.syncRequestSet) {
        this.syncConfirmUploadFileData = res.data as SMH.SyncConfirmUploadFileData;
      }
      this.emitProgress(this.info.size, { immediately: true });
      this.uploadMultiPartInfo = null;
      this.multiPartsMap = new Map<number, Uploader.PartsInfo>();
      this.multiPartsGroup = new Map<number, Set<number>>();
      finished = true;
    } catch (error: any) {
      // 正常退出 exit
      const exit = this.isFinished() || !error || error.message === 'canceled';
      if (!exit) {
        return Promise.reject({
          ...error,
          interface: 'SMH.confirmUploadFile',
          uploadType,
        });
      }
    }
    return finished;
  }

  /**
   * 百分比触发
   * @private
   */
  private _getPercent() {
    return (loaded: number, total: number) => {
      this.emit('ioPercent', { loaded, total });
    };
  }

  /**
   * @description 开始上传
   * @memberof Uploader
   */
  async start(restart?: boolean, collectToken?: string, uid?: string): Promise<void> {
    const startTime = new Date().getTime();
    // @ts-ignore
    const PartUploadFileSize = window.PartUploadFileSize || UploadConfig.PartUploadFileSize;
    const isSimpleUpload = this.info.size < PartUploadFileSize || collectToken;
    if (this.isFinished()) {
      this.setStatus(TaskStatus.success);
      this.emit('success', { uploadType: isSimpleUpload ? 'simple' : 'advanced' });
      return;
    }
    // 开始处理IO中
    this.setStatus(TaskStatus.preparing);

    // 文件夹的上传单独处理
    if (this.info.isDir) {
      await this.createFolder();
      return;
    }

    this.syncConfirmUploadFileData = undefined;
    if (!window.disableQuickUpload) {
      // 设置checking状态
      this.setStatus(TaskStatus.checking);
      const ret = await this.checkQuickUpload();
      if (ret) {
        logger.ui.info(`[Uploader] [${this.info.name}] checkQuickUpload hit`, this.info.localPath);
        this.setStatus(TaskStatus.success); // 设置才会触发 status_change
        // 秒传成功后通知完成
        this.setStatus(TaskStatus.success);
        this.emitProgress(this.info.size, { immediately: true });
        this.emit('success', {
          uploadType: 'quickUpload',
        });
        return;
      }
    }

    // 秒传检查后，再开始处理文件IO
    this.setStatus(TaskStatus.preparing);
    // 文件收集暂时不使用分片上传
    if (isSimpleUpload) {
      this.simpleUpload();
    } else {
      this.advanceUpload(startTime);
    }
  }

  /**
   * @description 暂停上传
   * @memberof Uploader
   */
  pause(): void {
    super.pause();
    this.emit('pause');
    this.startTimeStamp = Date.now();
  }

  /**
   * @description 取消上传正在上传中的文件
   * @param {boolean} [notChangeState] 不改变状态为取消态
   * @param {boolean} [isLogined]
   * @return {*}  {void}
   * @memberof UploaderClass
   */
  cancel(notChangeState?: boolean, isLogined?: boolean): void {
    super.cancel(notChangeState, isLogined);
    const { confirmKey } = this;
    if (!confirmKey) return;
    uploadCache.removeConfirmKey(confirmKey); // 取消时清理缓存
    isLogined &&
      this.getSMHAPISet()
        .cancelFileUpload({
          confirmKey,
          spaceId: this.info.spaceId,
          ...(typeof this.syncId === 'number' && {
            syncId: this.syncId,
          }),
        })
        .catch(error => {
          // 取消上传接口报错，不做任何处理
          const err = {
            ...error,
            interface: 'SMH.cancelFileUpload',
          };
          // throw new Error(err.message);
        });
    this.startTimeStamp = Date.now();
  }

  /**
   * @description 判断是否可以秒传
   * @memberof Uploader
   */
  async checkQuickUpload() {
    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;
    if (this.info.size <= UploadConfig.QuickUploadBeginSize) {
      return false;
    }
    this.emit('check_hash', {
      start: true,
      percent: 0,
    });
    let ret = false;
    try {
      ret = await this.startQuickUpload();
    } catch (error: MsgError) {
      logger.ui.error(`[Uploader] [${this.info.name}] checkQuickUpload error `, JSON.stringify(error));
      this.quickUploadError = error?.message || 'error';
    }
    this.quickUploadResult = ret ? 0 : 1;

    // if (!this.isRunning()) return false;
    // 这里是秒传检查结束后，再看一下进度
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;
    this.emit('check_hash', {
      start: false,
      percent: 0,
    });
    return ret;
  }
  static getQuickUploadPercent(offset: number, fileSize: number) {
    if (fileSize <= 0) {
      return 1;
    }
    let percent = Math.floor((offset / fileSize) * 100);
    if (percent >= 100) {
      percent = 99;
    }
    return percent;
  }
  async startQuickUpload() {
    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;

    const { remotePath, localPath, size: filesize, body } = this.info;
    const file = fileSlice(
      {
        filePath: localPath as string,
        body,
      },
      0,
      UploadConfig.QuickUploadBeginSize,
      UploadConfig.StreamHighWaterMark,
    );

    const beginningHash = await getFileSHA256(file, UploadConfig.QuickUploadBeginSize, (progress: number) => {
      const { size: filesize } = this.info;
      this.emit('check_hash', {
        start: true,
        percent: UploaderClass.getQuickUploadPercent(progress, filesize),
      });
      return TaskStatusUtil.isBeforeChecking(this.status, true);
    });
    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;
    let res = await this.requestSMHUploadFile({
      filePath: remotePath,
      filesize,
      conflictResolutionStrategy: this.conflictResolutionStrategy,
      spaceId: this.info.spaceId,
      ...(typeof this.syncId === 'number' && {
        syncId: this.syncId,
      }),
      cancelToken: this.cancelRequestSource.token,
      size: filesize.toString(),
      beginningHash,
      collectToken: store.getters.collectToken,
    });
    if (res.status !== 202) {
      logger.ui.info(`[Uploader] [${this.info.name}] startQuickUpload no wa`, this.info.localPath);
      return false;
    }

    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;
    this.emit('check_hash', {
      start: true,
      percent: UploaderClass.getQuickUploadPercent(UploadConfig.QuickUploadBeginSize, filesize),
    });

    // 首1MB hash命中继续校验剩余数据
    const fullHash = await this.getFileFullHash(beginningHash);
    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;

    if (!fullHash) {
      logger.ui.info(`[Uploader] [${this.info.name}] startQuickUpload no hit `, this.info.localPath, res.status);
      return false;
    }
    res = await this.requestSMHUploadFile({
      filePath: remotePath,
      filesize,
      conflictResolutionStrategy: this.conflictResolutionStrategy,
      spaceId: this.info.spaceId,
      ...(typeof this.syncId === 'number' && {
        syncId: this.syncId,
      }),
      cancelToken: this.cancelRequestSource.token,
      size: filesize.toString(),
      beginningHash,
      fullHash,
      collectToken: store.getters.collectToken,
    });
    // if (!this.isRunning()) return false;
    if (!TaskStatusUtil.isBeforeChecking(this.status, true)) return false;
    if (res.status !== 200) {
      logger.ui.info(
        `[Uploader] [${this.info.name}] startQuickUpload no fullHashString`,
        this.info.localPath,
        res.status,
      );

      return false;
    }
    if (!IS_BROWSER) {
      // 非浏览器这里多判断一下文件大小是否有变化
      try {
        const stats = await fsStat(localPath);
        if (stats.size !== filesize) {
          logger.ui.error(`[Uploader] [${this.info.name}] startQuickUpload file change`, stats.size, filesize);
          return false;
        }
      } catch (e) {
        return false;
      }
    }
    logger.ui.info(`[Uploader] [${this.info.name}] startQuickUpload hit`, this.info.localPath);
    return true;
  }
  onProgress(progress: number) {
    const { size: filesize } = this.info;
    this.emit('check_hash', {
      start: true,
      percent: UploaderClass.getQuickUploadPercent(progress, filesize),
    });
  }
  async getFileFullHash(beginningHash?: string) {
    const { localPath, size: filesize, body } = this.info;
    const file = fileSlice(
      {
        filePath: localPath as string,
        body,
      },
      UploadConfig.QuickUploadBeginSize,
      filesize,
      UploadConfig.StreamHighWaterMark,
    );

    const hash = await getFileSHA256(
      file,
      UploadConfig.QuickUploadBeginSize,
      (progress: number) => {
        const { size: filesize } = this.info;
        this.emit('check_hash', {
          start: true,
          percent: UploaderClass.getQuickUploadPercent(progress, filesize),
        });
        // return this.isRunning();
        return TaskStatusUtil.isBeforeChecking(this.status, true);
      },
      beginningHash,
    );

    return hash;
  }
  getQuickUploadInfo() {
    return {
      quickUploadResult: this.quickUploadResult,
      quickUploadError: this.quickUploadError,
    };
  }

  protected requestSMHUploadMultiPartFile(uploaFileParams: SMH.UploaFileParams) {
    return this.getSMHAPISet().uploadMultiPartFile(uploaFileParams);
  }

  protected requestSMHUploadFile(uploaFileParams: SMH.UploaFileParams) {
    return this.getSMHAPISet().uploadFile(uploaFileParams);
  }
}
